import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Github, House } from "lucide-react";
import { GitHubOrgSelector } from "../GitHubOrgSelector";
import { BlacksmithLogomarkSVG, BlacksmithWordmarkSVG } from "../BlacksmithLogoSVG";
import { CodesmithLottieIcon } from "@/components/icons/CodesmithLottieIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { SandboxesIcon } from "@/components/sandboxes/SandboxesIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { useAuth } from "@/components/AuthProvider";
import { useHasHoverPointer, useIsCompactViewport } from "@/hooks/use-device";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@repo/blacksmith-ui/cn";
import { AppHeaderBar } from "@/components/ui/app-header";
import { CollapsedIconTooltip, RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents, SidebarAdminRow, SidebarGlobalFooter } from "./SidebarGlobalChrome";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { SidebarBlockedOrgBanner, useSidebarGlobalBehaviors } from "./useSidebarGlobalBehaviors";
import { useAppRailHiddenOnMobile } from "./mobileRailVisibility";
import { useActiveApp, isHomePath, isStandaloneAccountPath, isAdminPath, settingsPagePath } from "./useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "./useCodesmithEnabledCache";
import { RAIL_WIDTH, RAIL_REDESIGN_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH_CSS_VAR, RAIL_WIDTH_TRANSITION_MS, RAIL_TRANSITION_EASING } from "./railConstants";
import { RAIL_SHORTCUT_LABELS, railShortcutSection, type RailShortcutSection } from "./railShortcuts";

/**
 * Slim app-switcher rail pinned to the left edge of the app. Hosts the
 * Blacksmith logo, top-level app entries (Home, GitHub Actions, Codesmith,
 * Proof), and the global footer controls (support, settings, notifications,
 * org switcher).
 *
 * The rail is 48px wide by default; clicking the logo pins it open at 200px
 * (row labels, org selector, full wordmark) until the logo is clicked again —
 * it does not auto-collapse on hover-out or navigation. The expanded rail is
 * part of the page layout: the contextual sidebar and main content slide
 * over in lockstep with the panel's own width transition (see `layoutWidth`
 * below and the matching `left` transition on the contextual Sidebar), rather
 * than being covered by an overlay.
 */

// Master switch for the rail's whole-panel hover expansion. Off by default:
// the rail stays 48px and each row surfaces its own tooltip, while the
// vCPU/status widget expands as a standalone hover overlay (RailStatusHoverCard)
// rather than growing the whole rail. Flip to true to restore the original
// behavior where hovering expands the entire rail to reveal row labels. Typed
// as boolean so both code paths stay type-checked at the default value.
const RAIL_HOVER_EXPAND: boolean = false;

// Hover-intent dwell before expanding. The pointer must rest on the rail for
// this long before the wipe starts, so a quick pass-over (e.g. on the way to
// the main content) never kicks off a half-open-then-reverse animation. Close
// stays instant, so the rail still feels snappy.
const RAIL_OPEN_DELAY_MS = 200;

// The mutually-exclusive rail rows that can read as active.
type RailSection = "home" | "actions" | "codesmith" | "proof" | "sandboxes" | "usage" | "admin";

// Slide/wipe transition for the expand/collapse. easeOutQuint (see
// RAIL_TRANSITION_EASING): the panel starts moving immediately and
// decelerates smoothly into its final width (an ease-out reads far smoother
// here than an ease-in-out, which hesitates then snaps). Duration/easing are
// shared with the contextual Sidebar's `left` transition (railConstants.ts)
// so the panel, the pushed main content, and the fixed-position contextual
// sidebar all slide in lockstep rather than one visibly lagging the others.
//
// This is an inline style, not a Tailwind class: Tailwind's content scanner
// only recognizes class strings that appear literally in source, and this
// value is assembled from imported constants, so a `[transition:...]`
// arbitrary-value class built from it would never get a matching CSS rule
// generated — the rail would silently skip the transition and jump straight
// to its new width. That's invisible on pages with a contextual Sidebar
// (its own `left` transition carries the visible slide), but on Codesmith
// pages, which push a plain flex sibling (SessionSidebar) with nothing else
// animating, it read as no transition at all.
const RAIL_TRANSITION_STYLE = `width ${RAIL_WIDTH_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;

// Set on navigation; hover expansion is ignored while true so the panel does
// not pop back open over the section the user just clicked into. Cleared once
// the pointer leaves the rail (a fresh hover re-enables expansion). Lives at
// module scope because the rail can still unmount and remount within a session
// (its Suspense boundary in SidebarLayout) and the flag must survive that
// remount.
let suppressExpandUntilPointerLeaves = false;

// Clears the post-navigation suppression once the pointer is actually off the
// rail. React's onMouseLeave can't be relied on for this: clicking a row
// collapses the panel (200→48px) instantly under a stationary cursor, and a
// resize out from under the pointer doesn't dispatch mouseleave — so the flag
// would otherwise stay armed and swallow the next genuine hover. A
// document-level mousemove checks the live pointer position instead.
//
// The listener lives at module scope, like the flag it clears: it must stay
// attached across any rail remount, because a mousemove missed in an
// instance-bound listener's teardown/re-arm gap could let the
// pointer travel off the rail unseen and then re-enter with the first
// delivered event already targeting the panel — leaving the flag stuck and
// hover dead until the pointer exits and re-enters. The rail is resolved from
// the DOM at event time (live rail or its suspense skeleton), so the check
// works whichever instance is mounted. If the rail leaves the page entirely,
// the first mousemove finds no rail, clears the flag, and removes the
// listener, so nothing lingers.
let suppressClearListener: ((ev: MouseEvent) => void) | null = null;
function removeSuppressClearListener() {
  if (suppressClearListener) {
    document.removeEventListener("mousemove", suppressClearListener);
    suppressClearListener = null;
  }
}

// Persists the logo-click pin (see `expanded`) across that same remount.
// Read once as the initial state on mount and written back on every change,
// so when the rail comes back after unmounting it resumes exactly how it was
// left, rather than resetting to collapsed each time.
let pinnedExpanded = false;

// `timeStamp` of the last keydown into a text field (see railShortcutSection).
// Module scope, like pinnedExpanded, so it survives rail remounts.
let lastTextEntryAt = -Infinity;
function armSuppressUntilPointerLeaves() {
  suppressExpandUntilPointerLeaves = true;
  removeSuppressClearListener();
  const check = (ev: MouseEvent) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('[data-slot="app-switcher-rail"], [data-slot="app-switcher-rail-skeleton"]')) {
      return;
    }
    suppressExpandUntilPointerLeaves = false;
    removeSuppressClearListener();
  };
  suppressClearListener = check;
  document.addEventListener("mousemove", check);
}
export default function AppSwitcherRail() {
  const $ = _c(186);
  const router = useRouter();
  const activeApp = useActiveApp();
  const routeSection = isAdminPath(router.pathname) ? "admin" : router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : activeApp === "codesmith" ? "codesmith" : activeApp === "proof" ? "proof" : activeApp === "sandboxes" ? "sandboxes" : "actions";
  const [optimisticSection, setOptimisticSection] = useState(null);
  const activeSection = optimisticSection ?? routeSection;
  const railRowNavigationRef = useRef(false);
  useIsCompactViewport();
  const hiddenOnMobile = useAppRailHiddenOnMobile(activeApp);
  const hasHoverPointer = useHasHoverPointer();
  const {
    codesmithAutomationsEnabled,
    orgsResolved,
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  const urlOrg = router.query.org;
  const {
    user
  } = useAuth();
  const urlInstanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  let t0;
  if ($[0] !== activeNamedOrg?.orgName || $[1] !== orgsResolved || $[2] !== urlInstanceSlug || $[3] !== urlOrg) {
    t0 = (typeof urlOrg === "string" ? urlOrg : undefined) || activeNamedOrg?.orgName || (orgsResolved ? undefined : getCachedLastActiveOrg(urlInstanceSlug)?.orgName);
    $[0] = activeNamedOrg?.orgName;
    $[1] = orgsResolved;
    $[2] = urlInstanceSlug;
    $[3] = urlOrg;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const currentOrg = t0;
  const homeHref = currentOrg ? `/${currentOrg}/home` : "/home";
  const runsJobsHref = currentOrg ? `/${currentOrg}/runs/jobs` : "/runs";
  const sidebarVariant = useCodesmithSidebarVariant(activeNamedOrg);
  const redesign = sidebarVariant === "redesign";
  const sessionsNewHref = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
  const proofHref = currentOrg ? `/${currentOrg}/proof` : "/proof";
  const sandboxesHref = currentOrg ? `/${currentOrg}/sandboxes` : "/sandboxes";
  const {
    activeGithubOrgBlocked: globalOrgBlocked,
    gPrefixActiveRef
  } = useSidebarGlobalBehaviors();
  const [expanded, setExpanded] = useState(pinnedExpanded);
  const slim = !expanded;
  const layoutWidth = expanded ? RAIL_EXPANDED_WIDTH : redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  const [instantClose, setInstantClose] = useState(false);
  const [codesmithRowHovered, setCodesmithRowHovered] = useState(false);
  const panelRef = useRef(null);
  const openTimerRef = useRef(null);
  const portalMouseListenerRef = useRef(null);
  const portalObserverRef = useRef(null);
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const clearOpenTimer = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (portalMouseListenerRef.current) {
        document.removeEventListener("mousemove", portalMouseListenerRef.current);
        portalMouseListenerRef.current = null;
      }
      if (portalObserverRef.current) {
        portalObserverRef.current.disconnect();
        portalObserverRef.current = null;
      }
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const removePortalTracker = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      removePortalTracker();
      const checkMouse = ev => {
        const target = ev.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const overPanel = panelRef.current?.contains(target);
        const overPortal = target.closest("[data-radix-popper-content-wrapper]");
        const portalOpen = document.querySelector("[data-radix-popper-content-wrapper]");
        if (!overPanel && !overPortal && !portalOpen) {
          setExpanded(false);
          removePortalTracker();
        }
      };
      portalMouseListenerRef.current = checkMouse;
      document.addEventListener("mousemove", checkMouse);
      const observer = new MutationObserver(() => {
        if (document.querySelector("[data-radix-popper-content-wrapper]")) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          setExpanded(false);
        }
        removePortalTracker();
      });
      observer.observe(document.body, {
        childList: true
      });
      portalObserverRef.current = observer;
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const installPortalTracker = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = section => e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      setOptimisticSection(section);
      railRowNavigationRef.current = true;
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleRailRowClick = t4;
  let t5;
  if ($[9] !== expanded || $[10] !== hasHoverPointer) {
    t5 = () => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
        return;
      }
      removePortalTracker();
      if (expanded) {
        return;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          return;
        }
        setExpanded(true);
      }, RAIL_OPEN_DELAY_MS);
    };
    $[9] = expanded;
    $[10] = hasHoverPointer;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      clearOpenTimer();
      suppressExpandUntilPointerLeaves = false;
      removeSuppressClearListener();
      const related = e_0.relatedTarget;
      if (related instanceof HTMLElement && related.closest("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      if (document.querySelector("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      setExpanded(false);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleMouseLeave = t6;
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      removePortalTracker();
      clearOpenTimer();
    };
    t8 = [removePortalTracker, clearOpenTimer];
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[15] !== router.events) {
    t9 = () => {
      const handleRouteChangeStart = () => {
        const railRowNavigation = railRowNavigationRef.current;
        railRowNavigationRef.current = false;
        if (railRowNavigation && panelRef.current?.matches(":hover")) {
          armSuppressUntilPointerLeaves();
        }
        clearOpenTimer();
        removePortalTracker();
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => {
        router.events.off("routeChangeStart", handleRouteChangeStart);
      };
    };
    t10 = [router.events, removePortalTracker, clearOpenTimer];
    $[15] = router.events;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[18] !== router.events) {
    t11 = () => {
      const settle = () => {
        if (RAIL_HOVER_EXPAND) {
          setInstantClose(true);
          setExpanded(false);
        }
        setOptimisticSection(null);
      };
      router.events.on("routeChangeComplete", settle);
      router.events.on("routeChangeError", settle);
      return () => {
        router.events.off("routeChangeComplete", settle);
        router.events.off("routeChangeError", settle);
      };
    };
    t12 = [router.events];
    $[18] = router.events;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[21] !== instantClose) {
    t13 = () => {
      if (!instantClose) {
        return;
      }
      const id = requestAnimationFrame(() => setInstantClose(false));
      return () => cancelAnimationFrame(id);
    };
    t14 = [instantClose];
    $[21] = instantClose;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[24] !== expanded) {
    t15 = () => {
      pinnedExpanded = expanded;
    };
    t16 = [expanded];
    $[24] = expanded;
    $[25] = t15;
    $[26] = t16;
  } else {
    t15 = $[25];
    t16 = $[26];
  }
  useLayoutEffect(t15, t16);
  let t17;
  let t18;
  if ($[27] !== layoutWidth) {
    t17 = () => {
      document.documentElement.style.setProperty(RAIL_WIDTH_CSS_VAR, `${layoutWidth}px`);
    };
    t18 = [layoutWidth];
    $[27] = layoutWidth;
    $[28] = t17;
    $[29] = t18;
  } else {
    t17 = $[28];
    t18 = $[29];
  }
  useLayoutEffect(t17, t18);
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [];
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  useLayoutEffect(_temp2, t19);
  let t20;
  let t21;
  if ($[31] !== codesmithEnabled || $[32] !== router || $[33] !== sessionsNewHref) {
    t20 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleNewChat = e_1 => {
        if (e_1.altKey && e_1.shiftKey && !e_1.metaKey && !e_1.ctrlKey && e_1.code === "KeyN") {
          e_1.preventDefault();
          setOptimisticSection("codesmith");
          router.push(sessionsNewHref);
        }
      };
      document.addEventListener("keydown", handleNewChat);
      return () => document.removeEventListener("keydown", handleNewChat);
    };
    t21 = [codesmithEnabled, sessionsNewHref, router];
    $[31] = codesmithEnabled;
    $[32] = router;
    $[33] = sessionsNewHref;
    $[34] = t20;
    $[35] = t21;
  } else {
    t20 = $[34];
    t21 = $[35];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[36] !== codesmithAutomationsEnabled || $[37] !== currentOrg || $[38] !== router) {
    t22 = () => {
      if (!codesmithAutomationsEnabled) {
        return;
      }
      const handleAutomations = e_2 => {
        if (e_2.altKey && e_2.shiftKey && !e_2.metaKey && !e_2.ctrlKey && e_2.code === "KeyA") {
          e_2.preventDefault();
          const automationsPath = currentOrg ? `/${currentOrg}/automations` : "/automations";
          setOptimisticSection("codesmith");
          router.push(automationsPath);
        }
      };
      document.addEventListener("keydown", handleAutomations);
      return () => document.removeEventListener("keydown", handleAutomations);
    };
    t23 = [codesmithAutomationsEnabled, currentOrg, router];
    $[36] = codesmithAutomationsEnabled;
    $[37] = currentOrg;
    $[38] = router;
    $[39] = t22;
    $[40] = t23;
  } else {
    t22 = $[39];
    t23 = $[40];
  }
  useEffect(t22, t23);
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [];
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  useEffect(_temp4, t24);
  const railShortcutsActive = routeSection !== "codesmith" && optimisticSection !== "codesmith";
  let t25;
  let t26;
  if ($[42] !== codesmithEnabled || $[43] !== gPrefixActiveRef || $[44] !== homeHref || $[45] !== proofHref || $[46] !== railShortcutsActive || $[47] !== router || $[48] !== runsJobsHref || $[49] !== sandboxesHref || $[50] !== sessionsNewHref) {
    t25 = () => {
      if (!railShortcutsActive) {
        return;
      }
      const hrefs = {
        home: homeHref,
        actions: runsJobsHref,
        codesmith: codesmithEnabled ? sessionsNewHref : null,
        proof: codesmithEnabled ? proofHref : null,
        sandboxes: sandboxesHref
      };
      const handleRailShortcut = e_4 => {
        if (gPrefixActiveRef.current) {
          return;
        }
        const section_0 = railShortcutSection(e_4, lastTextEntryAt);
        const href = section_0 && hrefs[section_0];
        if (!section_0 || !href) {
          return;
        }
        e_4.preventDefault();
        setOptimisticSection(section_0);
        router.push(href);
      };
      document.addEventListener("keydown", handleRailShortcut);
      return () => document.removeEventListener("keydown", handleRailShortcut);
    };
    t26 = [railShortcutsActive, homeHref, runsJobsHref, codesmithEnabled, sessionsNewHref, proofHref, sandboxesHref, gPrefixActiveRef, router];
    $[42] = codesmithEnabled;
    $[43] = gPrefixActiveRef;
    $[44] = homeHref;
    $[45] = proofHref;
    $[46] = railShortcutsActive;
    $[47] = router;
    $[48] = runsJobsHref;
    $[49] = sandboxesHref;
    $[50] = sessionsNewHref;
    $[51] = t25;
    $[52] = t26;
  } else {
    t25 = $[51];
    t26 = $[52];
  }
  useEffect(t25, t26);
  const t27 = currentOrg ?? undefined;
  let t28;
  if ($[53] !== globalOrgBlocked || $[54] !== t27) {
    t28 = <SidebarBlockedOrgBanner activeGithubOrgBlocked={globalOrgBlocked} currentOrg={t27} />;
    $[53] = globalOrgBlocked;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  const t29 = expanded ? "expanded" : "collapsed";
  const t30 = redesign && !expanded && RAIL_REDESIGN_CLASS;
  const t31 = hiddenOnMobile && "max-md:hidden";
  let t32;
  if ($[56] !== t30 || $[57] !== t31) {
    t32 = cn("group relative h-full shrink-0 flex flex-col overflow-hidden", "bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t30, t31);
    $[56] = t30;
    $[57] = t31;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  const t33 = instantClose ? "none" : RAIL_TRANSITION_STYLE;
  let t34;
  if ($[59] !== layoutWidth || $[60] !== t33) {
    t34 = {
      width: layoutWidth,
      transition: t33
    };
    $[59] = layoutWidth;
    $[60] = t33;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  const t35 = expanded ? "Collapse sidebar" : "Expand sidebar";
  let t36;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => setExpanded(_temp5);
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  const t37 = expanded ? "Collapse sidebar" : "Expand sidebar";
  const t38 = expanded ? "opacity-0" : "opacity-100";
  const t39 = instantClose ? "transition-none" : "[transition:opacity_100ms_ease-out]";
  let t40;
  if ($[63] !== t38 || $[64] !== t39) {
    t40 = cn("flex size-8 shrink-0 items-center justify-center", t38, t39);
    $[63] = t38;
    $[64] = t39;
    $[65] = t40;
  } else {
    t40 = $[65];
  }
  let t41;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div>;
    $[66] = t41;
  } else {
    t41 = $[66];
  }
  let t42;
  if ($[67] !== t40) {
    t42 = <div className={t40}>{t41}</div>;
    $[67] = t40;
    $[68] = t42;
  } else {
    t42 = $[68];
  }
  let t43;
  if ($[69] !== expanded || $[70] !== instantClose) {
    t43 = <div aria-hidden={true} className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2"><BlacksmithWordmarkSVG expanded={expanded} instant={instantClose} className="h-4 w-auto origin-left scale-[1.0485] text-[#0c0c0de5] dark:text-blacksmith-yellow" /></div>;
    $[69] = expanded;
    $[70] = instantClose;
    $[71] = t43;
  } else {
    t43 = $[71];
  }
  let t44;
  if ($[72] !== t37 || $[73] !== t42 || $[74] !== t43) {
    t44 = <button type="button" onClick={t36} aria-label={t37} className="relative flex h-8 w-full items-center hover:opacity-70 transition-opacity cursor-pointer">{t42}{t43}</button>;
    $[72] = t37;
    $[73] = t42;
    $[74] = t43;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] !== t35 || $[77] !== t44) {
    t45 = <AppHeaderBar><CollapsedIconTooltip label={t35} enabled={true}>{t44}</CollapsedIconTooltip></AppHeaderBar>;
    $[76] = t35;
    $[77] = t44;
    $[78] = t45;
  } else {
    t45 = $[78];
  }
  const t46 = redesign ? undefined : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t47;
  if ($[79] !== expanded) {
    t47 = <GitHubOrgSelector expanded={expanded} />;
    $[79] = expanded;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  let t48;
  if ($[81] !== t46 || $[82] !== t47) {
    t48 = <div className={t46}>{t47}</div>;
    $[81] = t46;
    $[82] = t47;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  const t49 = !redesign && "gap-1 pt-1";
  let t50;
  if ($[84] !== t49) {
    t50 = cn("rail-rows flex flex-col", t49);
    $[84] = t49;
    $[85] = t50;
  } else {
    t50 = $[85];
  }
  const t51 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.home : undefined;
  const t52 = currentOrg ? true : null;
  const t53 = !!currentOrg;
  let t54;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = handleRailRowClick("home");
    $[86] = t54;
  } else {
    t54 = $[86];
  }
  const t55 = activeSection === "home" ? "page" : undefined;
  const t56 = activeSection === "home" && RAIL_ROW_ACTIVE;
  let t57;
  if ($[87] !== t56) {
    t57 = cn(RAIL_ROW, t56);
    $[87] = t56;
    $[88] = t57;
  } else {
    t57 = $[88];
  }
  let t58;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <RailRowContents icon={<House className="size-[18px]" />} label="Home" />;
    $[89] = t58;
  } else {
    t58 = $[89];
  }
  let t59;
  if ($[90] !== homeHref || $[91] !== t52 || $[92] !== t53 || $[93] !== t55 || $[94] !== t57) {
    t59 = <RoutePrefetchLink href={homeHref} prefetch={t52} prefetchOnIntent={t53} onClick={t54} aria-current={t55} className={t57}>{t58}</RoutePrefetchLink>;
    $[90] = homeHref;
    $[91] = t52;
    $[92] = t53;
    $[93] = t55;
    $[94] = t57;
    $[95] = t59;
  } else {
    t59 = $[95];
  }
  let t60;
  if ($[96] !== slim || $[97] !== t51 || $[98] !== t59) {
    t60 = <CollapsedIconTooltip label="Home" enabled={slim} shortcut={t51}>{t59}</CollapsedIconTooltip>;
    $[96] = slim;
    $[97] = t51;
    $[98] = t59;
    $[99] = t60;
  } else {
    t60 = $[99];
  }
  const t61 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.actions : undefined;
  const t62 = currentOrg ? true : null;
  const t63 = !!currentOrg;
  let t64;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = handleRailRowClick("actions");
    $[100] = t64;
  } else {
    t64 = $[100];
  }
  const t65 = activeSection === "actions" ? "page" : undefined;
  const t66 = activeSection === "actions" && RAIL_ROW_ACTIVE;
  let t67;
  if ($[101] !== t66) {
    t67 = cn(RAIL_ROW, t66);
    $[101] = t66;
    $[102] = t67;
  } else {
    t67 = $[102];
  }
  let t68;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <RailRowContents icon={<Github className="size-[18px]" />} label="GitHub Actions" />;
    $[103] = t68;
  } else {
    t68 = $[103];
  }
  let t69;
  if ($[104] !== runsJobsHref || $[105] !== t62 || $[106] !== t63 || $[107] !== t65 || $[108] !== t67) {
    t69 = <RoutePrefetchLink href={runsJobsHref} prefetch={t62} prefetchOnIntent={t63} onClick={t64} aria-current={t65} className={t67}>{t68}</RoutePrefetchLink>;
    $[104] = runsJobsHref;
    $[105] = t62;
    $[106] = t63;
    $[107] = t65;
    $[108] = t67;
    $[109] = t69;
  } else {
    t69 = $[109];
  }
  let t70;
  if ($[110] !== slim || $[111] !== t61 || $[112] !== t69) {
    t70 = <CollapsedIconTooltip label="GitHub Actions" enabled={slim} shortcut={t61}>{t69}</CollapsedIconTooltip>;
    $[110] = slim;
    $[111] = t61;
    $[112] = t69;
    $[113] = t70;
  } else {
    t70 = $[113];
  }
  let t71;
  if ($[114] !== activeSection || $[115] !== codesmithEnabled || $[116] !== codesmithRowHovered || $[117] !== currentOrg || $[118] !== railShortcutsActive || $[119] !== sessionsNewHref || $[120] !== slim) {
    t71 = codesmithEnabled && (() => {
      const codesmithRow = <RoutePrefetchLink href={sessionsNewHref} prefetchOnIntent={!!currentOrg} onMouseEnter={() => setCodesmithRowHovered(true)} onMouseLeave={() => setCodesmithRowHovered(false)} onClick={handleRailRowClick("codesmith")} aria-current={activeSection === "codesmith" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "codesmith" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<CodesmithLottieIcon play={codesmithRowHovered} />} label="[code]smith" /></RoutePrefetchLink>;
      return <Tooltip><TooltipTrigger asChild={true}>{codesmithRow}</TooltipTrigger>{slim && <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">[code]smith</span>{railShortcutsActive && <ShortcutKeys keys={[RAIL_SHORTCUT_LABELS.codesmith]} />}</TooltipContent>}</Tooltip>;
    })();
    $[114] = activeSection;
    $[115] = codesmithEnabled;
    $[116] = codesmithRowHovered;
    $[117] = currentOrg;
    $[118] = railShortcutsActive;
    $[119] = sessionsNewHref;
    $[120] = slim;
    $[121] = t71;
  } else {
    t71 = $[121];
  }
  let t72;
  if ($[122] !== activeSection || $[123] !== codesmithEnabled || $[124] !== currentOrg || $[125] !== proofHref || $[126] !== railShortcutsActive || $[127] !== slim) {
    t72 = codesmithEnabled && <CollapsedIconTooltip label="Proof" enabled={slim} shortcut={railShortcutsActive ? RAIL_SHORTCUT_LABELS.proof : undefined}><RoutePrefetchLink href={proofHref} prefetchOnIntent={!!currentOrg} onClick={handleRailRowClick("proof")} aria-current={activeSection === "proof" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "proof" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<ProofIconBold className="size-[18px]" />} label="Proof" /></RoutePrefetchLink></CollapsedIconTooltip>;
    $[122] = activeSection;
    $[123] = codesmithEnabled;
    $[124] = currentOrg;
    $[125] = proofHref;
    $[126] = railShortcutsActive;
    $[127] = slim;
    $[128] = t72;
  } else {
    t72 = $[128];
  }
  const t73 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.sandboxes : undefined;
  const t74 = !!currentOrg;
  let t75;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = handleRailRowClick("sandboxes");
    $[129] = t75;
  } else {
    t75 = $[129];
  }
  const t76 = activeSection === "sandboxes" ? "page" : undefined;
  const t77 = activeSection === "sandboxes" && RAIL_ROW_ACTIVE;
  let t78;
  if ($[130] !== t77) {
    t78 = cn(RAIL_ROW, t77);
    $[130] = t77;
    $[131] = t78;
  } else {
    t78 = $[131];
  }
  let t79;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <RailRowContents icon={<SandboxesIcon className="size-[18px]" />} label="Sandboxes" />;
    $[132] = t79;
  } else {
    t79 = $[132];
  }
  let t80;
  if ($[133] !== sandboxesHref || $[134] !== t74 || $[135] !== t76 || $[136] !== t78) {
    t80 = <RoutePrefetchLink href={sandboxesHref} prefetchOnIntent={t74} onClick={t75} aria-current={t76} className={t78}>{t79}</RoutePrefetchLink>;
    $[133] = sandboxesHref;
    $[134] = t74;
    $[135] = t76;
    $[136] = t78;
    $[137] = t80;
  } else {
    t80 = $[137];
  }
  let t81;
  if ($[138] !== slim || $[139] !== t73 || $[140] !== t80) {
    t81 = <CollapsedIconTooltip label="Sandboxes" enabled={slim} shortcut={t73}>{t80}</CollapsedIconTooltip>;
    $[138] = slim;
    $[139] = t73;
    $[140] = t80;
    $[141] = t81;
  } else {
    t81 = $[141];
  }
  let t82;
  if ($[142] !== t50 || $[143] !== t60 || $[144] !== t70 || $[145] !== t71 || $[146] !== t72 || $[147] !== t81) {
    t82 = <nav className={t50} aria-label="Apps">{t60}{t70}{t71}{t72}{t81}</nav>;
    $[142] = t50;
    $[143] = t60;
    $[144] = t70;
    $[145] = t71;
    $[146] = t72;
    $[147] = t81;
    $[148] = t82;
  } else {
    t82 = $[148];
  }
  let t83;
  if ($[149] !== activeSection || $[150] !== expanded || $[151] !== redesign || $[152] !== slim) {
    t83 = !redesign && <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} />;
    $[149] = activeSection;
    $[150] = expanded;
    $[151] = redesign;
    $[152] = slim;
    $[153] = t83;
  } else {
    t83 = $[153];
  }
  let t84;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <div className="flex-1" />;
    $[154] = t84;
  } else {
    t84 = $[154];
  }
  let t85;
  if ($[155] !== activeSection || $[156] !== expanded || $[157] !== redesign || $[158] !== slim) {
    t85 = redesign ? <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} divider={false} /> : null;
    $[155] = activeSection;
    $[156] = expanded;
    $[157] = redesign;
    $[158] = slim;
    $[159] = t85;
  } else {
    t85 = $[159];
  }
  const t86 = currentOrg ?? undefined;
  const t87 = activeApp === "codesmith";
  let t88;
  if ($[160] !== currentOrg || $[161] !== t87) {
    t88 = settingsPagePath(currentOrg, t87);
    $[160] = currentOrg;
    $[161] = t87;
    $[162] = t88;
  } else {
    t88 = $[162];
  }
  let t89;
  if ($[163] !== activeSection || $[164] !== expanded || $[165] !== redesign || $[166] !== slim || $[167] !== t85 || $[168] !== t86 || $[169] !== t88) {
    t89 = <SidebarGlobalFooter leading={t85} expanded={expanded} currentOrg={t86} settingsHref={t88} activeSection={activeSection} onSectionClick={handleRailRowClick} className="pb-2" hideLabelWhenCollapsed={slim} statusAsHoverCard={slim} redesign={redesign} />;
    $[163] = activeSection;
    $[164] = expanded;
    $[165] = redesign;
    $[166] = slim;
    $[167] = t85;
    $[168] = t86;
    $[169] = t88;
    $[170] = t89;
  } else {
    t89 = $[170];
  }
  let t90;
  if ($[171] !== t45 || $[172] !== t48 || $[173] !== t82 || $[174] !== t83 || $[175] !== t89) {
    t90 = <div className="flex h-full w-full flex-col px-2">{t45}{t48}{t82}{t83}{t84}{t89}</div>;
    $[171] = t45;
    $[172] = t48;
    $[173] = t82;
    $[174] = t83;
    $[175] = t89;
    $[176] = t90;
  } else {
    t90 = $[176];
  }
  let t91;
  if ($[177] !== handleMouseEnter || $[178] !== t29 || $[179] !== t32 || $[180] !== t34 || $[181] !== t90) {
    t91 = <div ref={panelRef} data-slot="app-switcher-rail" data-state={t29} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t32} style={t34}>{t90}</div>;
    $[177] = handleMouseEnter;
    $[178] = t29;
    $[179] = t32;
    $[180] = t34;
    $[181] = t90;
    $[182] = t91;
  } else {
    t91 = $[182];
  }
  let t92;
  if ($[183] !== t28 || $[184] !== t91) {
    t92 = <>{t28}{t91}</>;
    $[183] = t28;
    $[184] = t91;
    $[185] = t92;
  } else {
    t92 = $[185];
  }
  return t92;
}
function _temp5(prev) {
  return !prev;
}
function _temp4() {
  const noteTextEntry = _temp3;
  document.addEventListener("keydown", noteTextEntry, true);
  return () => document.removeEventListener("keydown", noteTextEntry, true);
}
function _temp3(e_3) {
  if (isTypingTarget(e_3.target)) {
    lastTextEntryAt = e_3.timeStamp;
  }
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
}